import React, { useState } from 'react';
import { motion, Variants, AnimatePresence } from 'framer-motion';
import { ShoppingBag, Scissors, Star, MapPin, Clock, MessageCircle, Instagram, ArrowRight, X, Heart, Crown } from 'lucide-react';

const BasicBoutiqueLanding = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);

  const fadeUp: Variants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" as const } }
  };

  const handleOpenForm = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    setIsFormOpen(true);
  };

  return (
    <div className="bg-[#FAF7F2] text-stone-800 min-h-screen font-sans selection:bg-rose-300 selection:text-stone-900 overflow-x-hidden">
      
      {/* === Minimalist Elegant Navbar === */}
      <nav className="fixed top-0 w-full z-50 bg-[#FAF7F2]/90 backdrop-blur-md border-b border-stone-200/60 py-4 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex justify-between items-center">
          <div className="flex flex-col items-start cursor-pointer">
            <span className="font-serif text-2xl tracking-[0.15em] text-stone-900 uppercase leading-none">Elegance</span>
            <span className="text-[8px] tracking-[0.3em] text-stone-500 uppercase mt-1 font-bold">Boutique & Tailors</span>
          </div>
          
          <div className="hidden md:flex items-center gap-10 text-xs font-bold tracking-[0.15em] uppercase text-stone-500">
            <a href="#collections" className="hover:text-rose-600 transition-colors">Collections</a>
            <a href="#about" className="hover:text-rose-600 transition-colors">Legacy</a>
            <a href="#services" className="hover:text-rose-600 transition-colors">Services</a>
          </div>

          <a 
            href="https://wa.me/910000000000" 
            target="_blank" 
            rel="noreferrer"
            className="flex items-center gap-2 bg-stone-900 text-white px-6 py-2.5 rounded-none text-[10px] font-bold tracking-widest uppercase hover:bg-rose-600 transition-all shadow-md hover:shadow-xl"
          >
            <MessageCircle size={14} /> WhatsApp Us
          </a>
        </div>
      </nav>

      {/* === Upgraded Hero Section === */}
      <section className="relative pt-32 pb-20 px-6 lg:px-12 min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Soft background glow */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-rose-100/50 blur-[100px] rounded-full -z-10 pointer-events-none"></div>

        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center w-full">
          
          <motion.div initial="hidden" animate="visible" variants={fadeUp} className="order-2 lg:order-1 text-center lg:text-left z-10">
            <div className="inline-flex items-center gap-2 border border-rose-200 bg-white/50 text-rose-600 px-4 py-1.5 rounded-full text-[10px] font-bold tracking-[0.2em] uppercase mb-8 shadow-sm">
              <Star size={12} className="fill-rose-600" /> Festive Collection Arrived
            </div>
            <h1 className="text-5xl md:text-7xl font-serif text-stone-900 mb-6 leading-[1.1]">
              Crafting <span className="italic text-rose-500">Beauty,</span> <br/> Stitching <span className="italic text-stone-400">Grace.</span>
            </h1>
            <p className="text-stone-500 font-light text-lg mb-10 max-w-lg mx-auto lg:mx-0 leading-relaxed">
              Discover our exclusive range of designer ethnic wear, bridal lehengas, and experience flawless custom tailoring that fits you like a dream.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-6 justify-center lg:justify-start">
              <a href="#collections" className="bg-stone-900 text-white px-8 py-4 text-xs font-bold tracking-widest uppercase hover:bg-rose-600 transition-all flex items-center gap-3 shadow-lg shadow-stone-900/20 group">
                Explore Designs <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <button onClick={handleOpenForm} className="text-stone-900 border-b border-stone-900 pb-1 text-xs font-bold tracking-widest uppercase hover:text-rose-600 hover:border-rose-600 transition-colors">
                Book Tailoring
              </button>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1, ease: "easeOut" }} className="order-1 lg:order-2 relative flex justify-center lg:justify-end">
            {/* Decorative Elements */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[85%] h-[105%] border border-rose-200 rounded-t-full rounded-b-full -z-10 hidden md:block"></div>
            
            {/* Floating Badge */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }} className="absolute bottom-10 -left-10 bg-white p-4 shadow-xl border border-stone-100 flex items-center gap-4 z-20 hidden md:flex">
              <div className="w-12 h-12 bg-rose-50 rounded-full flex items-center justify-center text-rose-500"><Heart size={20} className="fill-rose-200"/></div>
              <div>
                <p className="text-stone-900 font-bold font-serif text-lg leading-none">500+</p>
                <p className="text-stone-500 text-[9px] uppercase tracking-widest font-bold mt-1">Happy Brides</p>
              </div>
            </motion.div>

            {/* Reliable Image */}
            <div className="relative w-full max-w-[450px] aspect-[3/4] rounded-t-full rounded-b-full overflow-hidden shadow-2xl border-4 border-white bg-stone-100">
              <img 
                src="https://images.unsplash.com/photo-1610030469983-98e550d6193c?q=80&w=800&auto=format&fit=crop" 
                alt="Designer Ethnic Wear" 
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
          </motion.div>

        </div>
      </section>

      {/* === Modern Marquee Section === */}
      <div className="bg-stone-900 text-rose-200 py-4 overflow-hidden flex whitespace-nowrap border-y border-stone-800">
        <motion.div 
          animate={{ x: ["0%", "-50%"] }} 
          transition={{ repeat: Infinity, ease: "linear", duration: 20 }}
          className="flex gap-8 text-xs font-bold tracking-[0.3em] uppercase"
        >
          {/* Repeated text for seamless loop */}
          {[...Array(6)].map((_, i) => (
             <span key={i} className="flex items-center gap-8">
               <span>Custom Tailoring</span> <Star size={10} className="fill-rose-200" />
               <span>Bridal Lehengas</span> <Star size={10} className="fill-rose-200" />
               <span>Ready-to-Wear</span> <Star size={10} className="fill-rose-200" />
               <span>Designer Suits</span> <Star size={10} className="fill-rose-200" />
             </span>
          ))}
        </motion.div>
      </div>

      {/* === Featured Categories === */}
      <section id="collections" className="py-24 px-6 lg:px-12 relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-rose-500 tracking-[0.2em] text-[10px] font-bold uppercase mb-4 block">Handpicked for you</span>
            <h2 className="text-4xl md:text-5xl font-serif text-stone-900 mb-6">Curated <span className="italic text-rose-400">Collections</span></h2>
            <div className="w-12 h-0.5 bg-stone-300 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              { title: "Women's Ethnic", sub: "Suits & Sarees", img: "https://cdn.shopify.com/s/files/1/0621/6751/5394/products/DK0656.1.jpg?v=1671776988" },
              { title: "Bridal Wear", sub: "Lehengas & Gowns", img: "https://i.pinimg.com/736x/78/cc/b3/78ccb380ab6ee5f69cc07ca247ad0655.jpg" },
              { title: "Festive Specials", sub: "Kurtis & Indo-Western", img: "https://images.unsplash.com/photo-1609505848912-b7c3b8b4beda?q=80&w=600&auto=format&fit=crop" }
            ].map((cat, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={fadeUp} className="group cursor-pointer">
                <div className="overflow-hidden rounded-none bg-stone-200 mb-6 aspect-[4/5] relative">
                  <img src={cat.img} alt={cat.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" />
                  <div className="absolute inset-0 bg-stone-900/10 group-hover:bg-transparent transition-colors"></div>
                </div>
                <div className="text-center">
                  <h3 className="font-serif text-2xl text-stone-900">{cat.title}</h3>
                  <p className="text-rose-500 text-[10px] font-bold tracking-widest uppercase mt-2">{cat.sub}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* === Legacy / About Section === */}
      <section id="about" className="py-24 bg-white px-6 lg:px-12 border-y border-stone-200/60">
        <div className="max-w-4xl mx-auto text-center">
          <Crown size={40} className="text-rose-300 mx-auto mb-8" />
          <h2 className="text-4xl md:text-5xl font-serif text-stone-900 mb-8 leading-tight">
            Serving elegance to our <br/> community <span className="italic text-rose-500">since 2010.</span>
          </h2>
          <p className="text-stone-500 leading-relaxed text-lg mx-auto">
            What started as a small tailoring shop has now blossomed into the city's most trusted boutique. We believe in the magic of premium fabrics, impeccable embroidery, and a fit that makes you feel confident. Every piece in our store tells a story of craftsmanship and love.
          </p>
        </div>
      </section>

      {/* === Services Section === */}
      <section id="services" className="py-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif text-stone-900 mb-6">Our <span className="italic text-rose-400">Expertise</span></h2>
            <div className="w-12 h-0.5 bg-stone-300 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {[
              { icon: <ShoppingBag size={32} />, title: "Ready-Made Garments", desc: "Walk in and pick from our hand-selected range of trendy and classic outfits ready to wear." },
              { icon: <Scissors size={32} />, title: "Custom Tailoring", desc: "Bring your own fabric or choose ours. Our master tailors ensure a flawless, custom-measured fit." },
              { icon: <Star size={32} />, title: "Bridal Consultation", desc: "Specialized appointments for brides-to-be. We customize heavy lehengas matching your dream vision." }
            ].map((srv, i) => (
              <div key={i} className="p-10 bg-white border border-stone-200/60 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-rose-200 scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-500"></div>
                <div className="w-16 h-16 bg-rose-50 rounded-full flex items-center justify-center text-rose-500 mx-auto mb-6 group-hover:bg-rose-500 group-hover:text-white transition-colors duration-500">{srv.icon}</div>
                <h3 className="font-serif text-2xl text-stone-900 mb-4">{srv.title}</h3>
                <p className="text-stone-500 font-light leading-relaxed text-sm">{srv.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === Contact & Footer === */}
      <footer className="bg-stone-950 text-white pt-24 pb-12 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 mb-16 border-b border-stone-800 pb-16">
          
          <div className="md:col-span-5">
            <span className="font-serif text-4xl tracking-[0.1em] uppercase leading-none block mb-6 text-white">Elegance</span>
            <p className="text-stone-400 font-light leading-relaxed mb-8 max-w-sm">
              Your one-stop destination for premium fashion, bespoke tailoring, and bridal perfection. We bring your dream outfits to reality.
            </p>
            <div className="flex gap-4 text-stone-400">
              <a href="#" className="hover:text-white transition-colors p-3 bg-stone-900 rounded-full hover:bg-rose-600"><Instagram size={20} /></a>
            </div>
          </div>

          <div className="md:col-span-4">
            <h4 className="text-[10px] font-bold tracking-widest uppercase mb-8 text-stone-500 border-b border-stone-800 pb-4 inline-block">Visit the Store</h4>
            <ul className="space-y-6 font-light text-stone-300 text-sm">
              <li className="flex items-start gap-4">
                <MapPin size={20} className="text-rose-500 shrink-0 mt-0.5" />
                <span>Shop No. 12, Fashion Street Market,<br/>City Center, 201001</span>
              </li>
              <li className="flex items-center gap-4">
                <Clock size={20} className="text-rose-500 shrink-0" />
                <span>Open Daily: 10:30 AM - 9:00 PM</span>
              </li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-[10px] font-bold tracking-widest uppercase mb-8 text-stone-500 border-b border-stone-800 pb-4 inline-block">Direct Contact</h4>
            <div className="bg-stone-900 p-6 rounded-none text-center border border-stone-800">
              <p className="text-stone-400 font-light mb-6 text-xs">Need alterations or a new outfit? Message us directly.</p>
              <a href="https://wa.me/910000000000" target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 bg-[#25D366] text-stone-950 py-3 px-6 font-bold text-xs uppercase tracking-widest hover:bg-white transition-colors w-full">
                <MessageCircle size={16} /> WhatsApp
              </a>
            </div>
          </div>

        </div>
        
        <div className="max-w-7xl mx-auto text-center text-stone-600 text-[10px] tracking-widest uppercase flex flex-col md:flex-row justify-between items-center gap-4">
          <span>© {new Date().getFullYear()} Elegance Boutique. All Rights Reserved.</span>
          <span>Designed with ❤️ for Demos</span>
        </div>
      </footer>

      {/* === Elegant Popup Form === */}
      <AnimatePresence>
        {isFormOpen && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsFormOpen(false)} className="absolute inset-0 bg-stone-950/80 backdrop-blur-sm"></motion.div>
            <motion.div initial={{ opacity: 0, y: 50, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.95 }} className="bg-[#FAF7F2] p-10 md:p-14 shadow-2xl relative w-full max-w-lg z-10 border border-stone-200">
              <button onClick={() => setIsFormOpen(false)} className="absolute top-6 right-6 text-stone-400 hover:text-stone-900 transition-colors"><X size={24}/></button>
              <h3 className="font-serif text-3xl md:text-4xl text-stone-900 mb-4">Book a Fitting</h3>
              <p className="text-stone-500 font-light text-sm mb-10 leading-relaxed">Leave your details and our master tailor will contact you to fix an appointment.</p>
              <form onSubmit={(e) => { e.preventDefault(); alert("Demo Form Submitted!"); setIsFormOpen(false); }} className="space-y-6">
                <div>
                   <input type="text" required placeholder="Full Name" className="w-full bg-white border-b border-stone-300 px-4 py-3 focus:outline-none focus:border-rose-500 transition-colors placeholder-stone-400" />
                </div>
                <div>
                   <input type="tel" required placeholder="Phone Number" className="w-full bg-white border-b border-stone-300 px-4 py-3 focus:outline-none focus:border-rose-500 transition-colors placeholder-stone-400" />
                </div>
                <div>
                   <select className="w-full bg-white border-b border-stone-300 px-4 py-3 focus:outline-none focus:border-rose-500 transition-colors text-stone-600 appearance-none cursor-pointer">
                     <option>New Custom Stitching</option>
                     <option>Alterations & Fitting</option>
                     <option>Bridal Consultation</option>
                   </select>
                </div>
                <button type="submit" className="w-full bg-stone-900 text-white font-bold tracking-[0.2em] uppercase text-xs py-5 hover:bg-rose-600 transition-colors mt-4 shadow-xl">Confirm Request</button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BasicBoutiqueLanding;